import { formatDate } from 'lib/helpers/dateUtils';
import numOrDefault from 'lib/helpers/numOrDefault';
import { ia, iaRa, mString } from 'lib/helpers/utility';

import { DATE_MEDIUM, DATE_SHORT } from 'components/shared/DocumentSheet/lib/docFormats';
import { amountToCents } from 'components/practice/BeyondBilling/Claims/CMS1500/lib/amountToCents';
import { SELF_PAY_ROW_TYPES } from 'components/practice/BeyondBilling/ElectronicInvoicing/lib/formatInvoiceProcedures';

import {
  CLAIMS_COLUMNS,
  HIDDEN_ALIASES,
  MONEY_KEYS,
  NEGATIVE_COLUMNS,
  OPTIONAL_KEYS,
  PORTRAIT_MONEY_LIMIT
} from './invoiceDocColumns';

export const LEGAL =
  'This Good Faith Estimate (GFE) under the No Surprises Act (§300gg-136) reflects your current insurance status. Your cost may change if coverage or deductible updates, you opt out of insurance, or additional services are provided. Questions? Contact our office.';

const TIERS = [
  { key: 'primary', label: 'Primary payer' },
  { key: 'secondary', label: 'Secondary payer' },
  { key: 'tertiary', label: 'Tertiary payer' }
];

const PR_LABELS = { 1: 'Ded.', 2: 'Coins.', 3: 'Copay' };

export const NA = '–';

export const money = (cents) => mString(numOrDefault(cents));
export const neg = (cents) => `- ${money(cents)}`;

export const negCell = (cents) => {
  const value = numOrDefault(cents);
  return value ? `–${money(value)}` : money(0);
};

export const dateOnly = (value, timezone) => (value ? formatDate(value, timezone, DATE_SHORT) : '');

export const dateMedium = (value, timezone) =>
  value ? formatDate(value, timezone, DATE_MEDIUM) : '';

export const memoText = (memo) =>
  typeof memo === 'string' ? memo : memo?.value || memo?.text || '';

export const hiddenSet = (hiddenFields) => new Set(iaRa(hiddenFields).filter(Boolean));

const itemDiscountCents = (item) =>
  item?.discount?.amount_cents ?? (typeof item?.discount === 'number' ? item.discount : 0);

const hasInsurance = (item) =>
  TIERS.some(({ key }) => ia(item?.[key])) || item?.isEstimated === true;

const latestAdjudication = (item) => {
  for (const { key } of [...TIERS].reverse()) {
    const entries = item?.[key];
    if (ia(entries)) return entries[entries.length - 1];
  }
  return null;
};

const payerName = (item, tierKey) => {
  const entries = iaRa(item?.[tierKey]);
  const entry = entries[entries.length - 1];
  return entry?.payer_name || entry?.payer || entry?.insurance_name || '';
};

const benefitSegments = (item) => {
  const entry = latestAdjudication(item);
  if (!entry) return [];

  const byCode = new Map();

  [...iaRa(entry.ins_adj), ...iaRa(entry.pt_adj)].forEach((adjustment) => {
    const group = String(adjustment?.group || '').toUpperCase();
    const label = PR_LABELS[String(adjustment?.code || '').trim()];
    if (group !== 'PR' || !label) return;

    byCode.set(label, numOrDefault(byCode.get(label)) + amountToCents(adjustment?.amount));
  });

  return [...byCode.entries()]
    .filter(([, amount]) => amount)
    .map(([label, amount]) => `${label} ${money(amount)}`);
};

// Product and package lines share the service row layout, but insurance never applies to them:
// they read as their kind rather than as a self-pay service.
const CATALOG_KINDS = { product: 'Product', package: 'Package' };

const catalogKind = (item) => CATALOG_KINDS[String(item?.type || '').toLowerCase()];

const selfPaySegment = (item) => {
  const isSelfPay = SELF_PAY_ROW_TYPES.includes(String(item?.type || '').toLowerCase());
  const selfPayDiscount = numOrDefault(item?.charge) - numOrDefault(item?.ptBalance);

  return isSelfPay && selfPayDiscount > 0
    ? `Self-pay · Self-pay discount ${money(selfPayDiscount)}`
    : 'Self-pay · full charge is patient responsibility';
};

export const serviceMeta = (item, timezone) => {
  const dos = dateOnly(item?.dateofservice, timezone);
  const segments = dos ? [`DOS ${dos}`] : [];
  const kind = catalogKind(item);

  if (kind) {
    const quantity = numOrDefault(item?.quantity);
    segments.push(quantity > 1 ? `${kind} · Qty ${quantity}` : kind);
    return segments.join(' · ');
  }

  const benefits = benefitSegments(item);

  if (!hasInsurance(item)) segments.push(selfPaySegment(item));
  else if (benefits.length) segments.push(...benefits);
  else if (!numOrDefault(item?.ptBalance)) segments.push('No patient responsibility');

  const line = segments.join(' · ');

  return item?.isEstimated && line ? `${line} (estimated)` : line;
};

export const serviceTitle = (item, hidden) =>
  [
    hidden?.has('procedure_code')
      ? ''
      : (item?.procedure_code ?? (catalogKind(item) ? item?.code : null)),
    item?.details || item?.name
  ]
    .filter(Boolean)
    .join(' · ');

export const dxCodes = (item) => iaRa(item?.icd_10_codes).filter(Boolean).join(', ');

export const itemAmounts = (item, { claims }) => {
  const insured = claims && hasInsurance(item);

  return {
    charge: numOrDefault(item?.charge),
    ins_adjustment: insured ? numOrDefault(item?.ins_adjustment) : null,
    allowed_amount: insured ? numOrDefault(item?.allowed_amount) : null,
    ins_payment: insured ? numOrDefault(item?.ins_payment) : null,
    ins_balance: insured ? numOrDefault(item?.ins_balance) : null,
    disc_wo: numOrDefault(itemDiscountCents(item)) + numOrDefault(item?.write_off),
    state_tax: numOrDefault(item?.state_tax),
    local_tax: numOrDefault(item?.local_tax),
    pt_paid: numOrDefault(item?.pt_paid ?? item?.amount_paid),
    ptBalance: numOrDefault(item?.ptBalance)
  };
};

export const tierAmounts = (child) => {
  const adjustment = numOrDefault(child?.ins_adjustment);
  const charge = numOrDefault(child?.charge);

  return {
    charge: null,
    ins_adjustment: adjustment || null,
    allowed_amount: adjustment ? charge - adjustment : null,
    ins_payment: numOrDefault(child?.ins_payment),
    ins_balance: numOrDefault(child?.ins_balance),
    disc_wo: null,
    state_tax: null,
    local_tax: null,
    pt_paid: null,
    ptBalance: null
  };
};

export const cellValue = (key, amount) => {
  if (amount == null) return NA;
  return NEGATIVE_COLUMNS.has(key) ? negCell(amount) : money(amount);
};

export const tierChildren = (item) =>
  TIERS.filter(({ key }) => ia(item?.[key])).map(({ key, label }, index) => ({
    label: [label, payerName(item, key)].filter(Boolean).join(' · '),
    child: iaRa(item?.children)[index]
  }));

export const visibleMoneyKeys = ({ rows, hidden, claims }) => {
  const totals = {};
  MONEY_KEYS.forEach((key) => {
    totals[key] = 0;
  });

  iaRa(rows).forEach((item) => {
    const amounts = itemAmounts(item, { claims });
    MONEY_KEYS.forEach((key) => {
      totals[key] += Math.abs(numOrDefault(amounts[key]));
    });
  });

  const aliased = new Set([...hidden].map((field) => HIDDEN_ALIASES[field] || field));

  return MONEY_KEYS.filter((key) => {
    if (aliased.has(key)) return false;
    if (!claims && CLAIMS_COLUMNS.has(key)) return false;
    if (OPTIONAL_KEYS.includes(key)) return totals[key] > 0;
    return true;
  });
};

export const isLandscape = (keys) => keys.length > PORTRAIT_MONEY_LIMIT;
